import React, { useState, useEffect } from 'react';
import { useLocation } from '../context/LocationContext';
import { api } from '../services/apiService';
import './LocationSelector.css';

const LocationSelector = ({ onLocationSelected, showTitle = true, isModal = false }) => {
  const { selectedLocation, saveLocation } = useLocation();
  const [cities, setCities] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedApartment, setSelectedApartment] = useState('');
  const [loadingCities, setLoadingCities] = useState(true);
  const [loadingApartments, setLoadingApartments] = useState(false);

  // Fetch cities on mount
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await api.get('/cities');
        const cityList = response.data || [];
        setCities(cityList);

        // Restore saved city selection
        if (selectedLocation?.cityId) {
          const match = cityList.find(c => c.id.toString() === selectedLocation.cityId.toString());
          if (match) setSelectedCity(match.id.toString());
        }
      } catch (err) {
        console.error('Failed to load cities:', err);
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch apartments when city changes
  useEffect(() => {
    if (!selectedCity) {
      setApartments([]);
      setSelectedApartment('');
      return;
    }
    const fetchApartments = async () => {
      setLoadingApartments(true);
      try {
        const response = await api.get('/apartments', { city_id: selectedCity });
        const aptList = response.data || [];
        setApartments(aptList);

        // Restore saved apartment selection
        if (selectedLocation?.apartmentId) {
          const match = aptList.find(a => a.id.toString() === selectedLocation.apartmentId.toString());
          if (match) setSelectedApartment(match.id.toString());
        }
      } catch (err) {
        console.error('Failed to load apartments:', err);
      } finally {
        setLoadingApartments(false);
      }
    };
    fetchApartments();
  }, [selectedCity]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
    setSelectedApartment('');
  };

  const handleApartmentChange = (e) => {
    const apartmentId = e.target.value;
    setSelectedApartment(apartmentId);

    if (apartmentId && selectedCity) {
      const city = cities.find(c => c.id.toString() === selectedCity);
      const apartment = apartments.find(a => a.id.toString() === apartmentId);

      if (city && apartment) {
        const locationData = {
          cityId: city.id,
          cityName: city.city,
          apartmentId: apartment.id,
          apartmentName: apartment.apartment,
          address: apartment.address,
          pin: apartment.pin || apartment.zip,
        };
        saveLocation(locationData);
        if (onLocationSelected) onLocationSelected(locationData);
      }
    }
  };

  const selectedCityObj = cities.find(c => c.id.toString() === selectedCity);
  const selectedApartmentObj = apartments.find(a => a.id.toString() === selectedApartment);

  return (
    <div className={`location-selector ${isModal ? 'location-selector-modal' : ''}`}>
      {showTitle && <h2 className="location-selector-title">Select Location & Apartment</h2>}

      <div className="dropdown-container">
        <label htmlFor="city-select" className="dropdown-label">Location (City):</label>
        <select
          id="city-select"
          className="dropdown-select"
          value={selectedCity}
          onChange={handleCityChange}
          disabled={loadingCities}
        >
          <option value="">{loadingCities ? 'Loading cities...' : '-- Select a Location --'}</option>
          {cities.map(city => (
            <option key={city.id} value={city.id}>{city.city}</option>
          ))}
        </select>
      </div>

      <div className="dropdown-container">
        <label htmlFor="apartment-select" className="dropdown-label">Apartment:</label>
        <select
          id="apartment-select"
          className="dropdown-select"
          value={selectedApartment}
          onChange={handleApartmentChange}
          disabled={!selectedCity || loadingApartments}
        >
          <option value="">
            {!selectedCity
              ? '-- Please select a location first --'
              : loadingApartments
                ? 'Loading apartments...'
                : apartments.length === 0
                  ? '-- No apartments available --'
                  : '-- Select an Apartment --'}
          </option>
          {apartments.map(apt => (
            <option key={apt.id} value={apt.id}>{apt.apartment}</option>
          ))}
        </select>
      </div>

      {selectedApartmentObj && (
        <div className="selected-info">
          <h3>Selected Details:</h3>
          <div className="info-item"><strong>City:</strong> {selectedCityObj?.city}</div>
          <div className="info-item"><strong>Apartment:</strong> {selectedApartmentObj.apartment}</div>
          <div className="info-item"><strong>Address:</strong> {selectedApartmentObj.address}</div>
          {(selectedApartmentObj.pin || selectedApartmentObj.zip) && (
            <div className="info-item"><strong>PIN/ZIP:</strong> {selectedApartmentObj.pin || selectedApartmentObj.zip}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
